import React, { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';
import { useScreenTransition } from '@/hooks/useScreenTransition';
import { safeWrite } from '@/utils/supabaseWrite';
import { hapticMilestone, hapticTap } from '@/utils/haptics';
import { playSessionCompleteChime } from '@/utils/sound';
import { firstNameFromUser } from '@/utils/nameFromUser';
import OnboardingHeader from '@/components/shared/OnboardingHeader';

// ── Illustrations ────────────────────────────────────────────────────────
// Hand-drawn, warm and feminine. All paths use the brand palette:
// rose #A8003F, soft pink #FDF0F4, warm brown #8B7355. SVGs render
// inline so they ship with the component and do not need a build step.

function HeroIllustration() {
  // Three concentric organic shapes suggesting the pelvic floor from
  // above — hand-drawn almond forms rather than perfect ellipses. The
  // outermost shape is a warm wash of soft pink; the middle is a rose
  // contour; the innermost is a warm-brown seed contour with a single
  // gentle breath-curve beneath grounding the composition.
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 16px' }} aria-hidden="true">
      <svg style={{ color: 'var(--primary)' }} width="240" height="156" viewBox="0 0 240 156" fill="none" role="img" aria-label="">
        <defs>
          <radialGradient id="hero-glow" cx="50%" cy="46%" r="55%">
            <stop offset="0%" stopColor="#FDF0F4" stopOpacity="1" />
            <stop offset="100%" stopColor="#FDF0F4" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Ambient warm glow */}
        <ellipse cx="120" cy="72" rx="112" ry="68" fill="url(#hero-glow)" />

        {/* Outer almond — soft pink wash, slightly asymmetric */}
        <path
          d="M120 18 C 62 20, 20 46, 20 76 C 20 108, 64 132, 120 132 C 178 132, 222 108, 220 76 C 218 46, 176 20, 120 18 Z"
          fill="#FDF0F4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeOpacity="0.45"
          strokeLinejoin="round"
        />

        {/* Middle ring — rose contour, breathing outward */}
        <path
          d="M120 36 C 78 38, 46 56, 46 76 C 46 98, 80 114, 120 114 C 162 114, 194 98, 192 76 C 190 56, 158 38, 120 36 Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeOpacity="0.7"
          strokeLinejoin="round"
        />

        {/* Inner seed — warm brown, the sensed centre */}
        <path
          d="M120 56 C 98 57, 82 66, 82 76 C 82 87, 100 96, 120 96 C 140 96, 158 86, 156 76 C 154 66, 138 55, 120 56 Z"
          fill="none"
          stroke="#8B7355"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />

        {/* Breath curve grounding the composition */}
        <path
          d="M52 142 C 88 136, 150 136, 188 142"
          fill="none"
          stroke="#8B7355"
          strokeWidth="1.5"
          strokeOpacity="0.5"
          strokeLinecap="round"
        />

        {/* Single tender dot at centre — the quiet focus */}
        <circle cx="120" cy="76" r="2.5" fill="currentColor" />
      </svg>
    </div>
  );
}

// Q1 option icons. Each takes a `selected` prop so its interior stroke
// can warm from brown → rose when the card is picked. Rendered in a
// 32×32 viewBox inside a 52×52 tile.

function IconReady({ selected }) {
  // A sprout opening upward under a small sun — "ready".
  const stroke = selected ? 'currentColor' : '#8B7355';
  return (
    <svg style={{ color: 'var(--primary)' }} width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="6.5" r="2" fill={stroke} />
      <path d="M16 11 C 16 16, 16 22, 16 27" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M16 17 C 13 15, 10 16, 10 19 C 10 21, 13 22, 16 20" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 21 C 19 19, 22 20, 22 23 C 22 25, 19 26, 16 24" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTension({ selected }) {
  // A gentle wave with a soft cinch — "some tension".
  const stroke = selected ? 'currentColor' : '#8B7355';
  return (
    <svg style={{ color: 'var(--primary)' }} width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M4 14 C 8 10, 10 10, 13 13 C 14 14, 14 16, 13 17 C 11 19, 11 21, 14 22" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 22 C 17 20, 19 20, 21 17 C 22 16, 22 14, 21 13 C 19 10, 22 10, 28 14" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="17" cy="17" r="1.25" fill={stroke} />
    </svg>
  );
}

function IconCare({ selected }) {
  // A lotus petal opening — tender care, softening.
  const stroke = selected ? 'currentColor' : '#8B7355';
  return (
    <svg style={{ color: 'var(--primary)' }} width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M16 6 C 13 12, 13 18, 16 24 C 19 18, 19 12, 16 6 Z" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 24 C 9 22, 6 17, 6 12 C 10 14, 14 18, 16 24 Z" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 24 C 23 22, 26 17, 26 12 C 22 14, 18 18, 16 24 Z" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 26 C 13 24, 19 24, 24 26" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.55" />
    </svg>
  );
}

function IconRecovery({ selected }) {
  // A waning crescent with a small seed — recovery, postpartum, rebirth.
  const stroke = selected ? 'currentColor' : '#8B7355';
  return (
    <svg style={{ color: 'var(--primary)' }} width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M21 6 C 14 8, 10 13, 10 18 C 10 23, 14 27, 21 28 C 15 24, 13 21, 13 17 C 13 13, 15 10, 21 6 Z" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="25" cy="12" r="1.5" fill={stroke} />
    </svg>
  );
}

// Movement trio icons for the Path A result screen. Each honours its
// established brand colour (--squeeze / --relax / --push) so the result
// screen reads the same visual language as the exercise player and
// progress views.

function IconSqueeze({ color }) {
  // Ring with three small chevrons pressing inward from top / left /
  // right — the tip of each chevron points toward the centre.
  return (
    <svg style={{ color: 'var(--primary)' }} width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="7.5" stroke={color} strokeWidth="1.75" />
      <path d="M13 3 L16 6 L19 3" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M29 13 L26 16 L29 19" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 13 L6 16 L3 19" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconRelax({ color }) {
  // A soft dashed ring at neutral — resting, open, not working.
  return (
    <svg style={{ color: 'var(--primary)' }} width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="9" stroke={color} strokeWidth="1.75" strokeDasharray="3 3" />
      <circle cx="16" cy="16" r="1.5" fill={color} />
    </svg>
  );
}

function IconPush({ color }) {
  // Ring with three small chevrons opening outward from top / left /
  // right — the tip of each chevron points away from the centre.
  return (
    <svg style={{ color: 'var(--primary)' }} width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="7.5" stroke={color} strokeWidth="1.75" />
      <path d="M13 6 L16 3 L19 6" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M26 13 L29 16 L26 19" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 13 L3 16 L6 19" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Bullet card icons (Rule 3) ──────────────────────────────────────────
// A small library of line icons for bullet-card rows on the intro and
// result screens. All share the same 24x24 viewBox + rose stroke.

function IconCompass() {
  return (
    <svg style={{ color: 'var(--primary)' }} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.5 8.5 L13 13 L8.5 15.5 L11 11 Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSprout() {
  return (
    <svg style={{ color: 'var(--primary)' }} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20 V 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 12 C 8 12, 5 10, 5 6 C 9 6, 12 8, 12 12 Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 14 C 16 14, 19 12, 19 8 C 15 8, 12 10, 12 14 Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 20 H 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg style={{ color: 'var(--primary)' }} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 7 V 12 L 15 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTarget() {
  return (
    <svg style={{ color: 'var(--primary)' }} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPath() {
  return (
    <svg style={{ color: 'var(--primary)' }} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 4 C 10 8, 14 8, 18 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 12 C 10 16, 14 16, 18 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 20 C 10 18, 14 18, 18 20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg style={{ color: 'var(--primary)' }} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 8 H 20 V 19 C 20 20, 19 20.5, 18 20.5 H 6 C 5 20.5, 4 20, 4 19 Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 8 V 6 C 4 5, 5 4.5, 6 4.5 H 18 C 19 4.5, 20 5, 20 6 V 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 3 V 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 3 V 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 12 H 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 12 H 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 16 H 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Q2 option icons (Rule 3) — small semantic glyphs so question 2 has
// the same "icon + label" rhythm as question 1.

function IconCalendarCheck({ selected }) {
  const stroke = selected ? 'currentColor' : '#8B7355';
  return (
    <svg style={{ color: 'var(--primary)' }} width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M5 10 H 27 V 25 C 27 26, 26 26.5, 25 26.5 H 7 C 6 26.5, 5 26, 5 25 Z" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 10 V 8 C 5 7, 6 6.5, 7 6.5 H 25 C 26 6.5, 27 7, 27 8 V 10" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 4 V 8" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M22 4 V 8" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M11 18 L 15 22 L 21 15" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSpark({ selected }) {
  const stroke = selected ? 'currentColor' : '#8B7355';
  return (
    <svg style={{ color: 'var(--primary)' }} width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M16 6 V 12" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M16 20 V 26" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M6 16 H 12" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M20 16 H 26" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="16" cy="16" r="3" stroke={stroke} strokeWidth="1.75" />
    </svg>
  );
}

function IconSeed({ selected }) {
  const stroke = selected ? 'currentColor' : '#8B7355';
  return (
    <svg style={{ color: 'var(--primary)' }} width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M16 8 C 12 12, 12 20, 16 24 C 20 20, 20 12, 16 8 Z" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 26 C 12 24, 20 24, 26 26" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.6" />
    </svg>
  );
}

// Path B specific icons — for the two-pill row on the Path B result
// screen, where the programme opens with breath and release rather
// than the full movement trio.

function IconBreath({ color }) {
  // A gentle breath cycle — two paired arcs suggesting inhale lifting
  // and exhale settling, with a small quiet dot at centre.
  return (
    <svg style={{ color: 'var(--primary)' }} width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M5 13 C 9 7, 15 7, 16 13" stroke={color} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M16 13 C 17 7, 23 7, 27 13" stroke={color} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M5 21 C 9 27, 15 27, 16 21" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" />
      <path d="M16 21 C 17 27, 23 27, 27 21" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" />
      <circle cx="16" cy="17" r="1.5" fill={color} />
    </svg>
  );
}

function IconRelease({ color }) {
  // A petal softening downward toward a grounding line — letting go,
  // the nervous system settling.
  return (
    <svg style={{ color: 'var(--primary)' }} width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M16 5 C 20 10, 22 16, 16 22 C 10 16, 12 10, 16 5 Z" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 26 C 13 24, 19 24, 25 26" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.55" />
      <circle cx="16" cy="14" r="1.25" fill={color} />
    </svg>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────

const s = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'var(--cream, #FAF6F1)',
    display: 'flex',
    flexDirection: 'column',
  },
  progressTrack: {
    flexShrink: 0,
    height: 3,
    width: '100%',
    background: 'var(--beige, #E8DDD0)',
    marginTop: 'var(--safe-top, env(safe-area-inset-top, 0px))',
  },
  progressFill: {
    height: '100%',
    background: 'var(--gold, #af004f)',
    transition: 'width 300ms ease',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '32px 24px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontFamily: 'var(--font-title)',
    fontSize: 40,
    fontWeight: 500,
    fontStyle: 'italic',
    color: 'var(--dark-brown)',
    lineHeight: 1.08,
    letterSpacing: '-0.02em',
    margin: '8px 0 18px',
    fontVariationSettings: '"opsz" 96',
  },
  eyebrow: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--gold, #af004f)',
    margin: '4px 0 6px',
  },
  subtitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 18,
    fontWeight: 400,
    color: 'var(--warm-brown)',
    lineHeight: 1.65,
    marginBottom: 24,
  },
  body: {
    fontFamily: 'var(--font-body)',
    fontSize: 18,
    fontWeight: 400,
    color: 'var(--text)',
    lineHeight: 1.7,
    marginBottom: 32,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
  },
  option: {
    display: 'flex',
    alignItems: 'stretch',
    textAlign: 'left',
    width: '100%',
    background: 'var(--nude, #F5EDE3)',
    border: '1.5px solid var(--beige, #E8DDD0)',
    borderRadius: 'var(--radius-md, 12px)',
    padding: 0,
    cursor: 'pointer',
    transition: 'background 200ms ease, border-color 200ms ease, transform 100ms ease',
    fontFamily: 'inherit',
    WebkitTapHighlightColor: 'transparent',
    overflow: 'hidden',
  },
  optionSelected: {
    background: 'var(--beige, #E8DDD0)',
    borderColor: 'var(--gold, #af004f)',
  },
  optionBar: {
    width: 4,
    flexShrink: 0,
    background: 'transparent',
    transition: 'background 200ms ease',
  },
  optionBarSelected: {
    background: 'var(--gold, #af004f)',
  },
  iconTile: {
    flexShrink: 0,
    width: 56,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#FDF0F4',
    transition: 'background 200ms ease',
  },
  iconTileSelected: {
    background: '#fff',
  },
  optionText: {
    flex: 1,
    padding: '16px 18px',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.55,
    color: 'var(--dark-brown, #5A3E2B)',
  },
  footer: {
    flexShrink: 0,
    background: 'var(--cream, #FAF6F1)',
    padding: '12px 24px calc(16px + env(safe-area-inset-bottom, 0px))',
  },
  cta: {
    display: 'block',
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    padding: '18px 16px',
    border: 'none',
    borderRadius: 'var(--radius-md, 12px)',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textAlign: 'center',
    transition: 'background 200ms ease, opacity 200ms ease',
    touchAction: 'manipulation',
  },
  ctaEnabled: {
    background: 'var(--gold, #af004f)',
    color: '#fff',
    cursor: 'pointer',
    opacity: 1,
  },
  ctaDisabled: {
    background: 'var(--surface-high, #E8DDD0)',
    color: 'var(--text-secondary, #6B5A4A)',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  bulletStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
    flex: 1,
  },
  bulletCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    background: '#fff',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 12,
    padding: 14,
  },
  bulletIconWrap: {
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'var(--primary-pale, #F5D6E0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bulletText: {
    flex: 1,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.5,
    color: 'var(--text, #3D2B1F)',
    paddingTop: 4,
  },
  bulletStrong: {
    fontWeight: 600,
    color: 'var(--dark-brown, #5A3E2B)',
  },
  movementRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 10,
    marginTop: 8,
    marginBottom: 24,
  },
  movementRowDuo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    marginTop: 8,
    marginBottom: 24,
  },
  movementPill: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '14px 8px',
    borderRadius: 'var(--radius-md, 12px)',
    background: 'var(--nude, #F5EDE3)',
    border: '1px solid var(--beige, #E8DDD0)',
  },
  movementIconTile: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
  },
  movementLabel: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  intentEcho: {
    fontFamily: "var(--font-title, 'Playfair Display', 'Cormorant Garamond', Georgia, serif)",
    fontStyle: 'italic',
    fontSize: 16,
    lineHeight: 1.55,
    color: 'var(--warm-brown, #8B7355)',
    margin: '0 0 20px',
  },
  whyDetails: {
    background: 'var(--surface, #FFFFFF)',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 12,
    padding: '4px 16px',
    marginTop: 4,
    marginBottom: 8,
  },
  whySummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 0',
    cursor: 'pointer',
    listStyle: 'none',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--gold, #af004f)',
    WebkitTapHighlightColor: 'transparent',
  },
  whyChevron: {
    fontSize: 14,
    transition: 'transform 200ms var(--ease-soft, ease)',
    color: 'var(--warm-brown, #8B7355)',
  },
  whyBody: {
    margin: '0 0 12px',
    fontFamily: 'var(--font-body, "Newsreader", Georgia, serif)',
    fontSize: 15,
    lineHeight: 1.65,
    color: 'var(--dark-brown, #5A3E2B)',
  },
};

// A single benefit/result bullet card: rose icon on the left in a pale
// circle, body text on the right. Used on intro + result sub-screens.

function BulletCard({ Icon, title, children, className, style }) {
  return (
    <div className={className} style={{ ...s.bulletCard, ...style }}>
      <span style={s.bulletIconWrap} aria-hidden="true">
        <Icon />
      </span>
      <span style={s.bulletText}>
        {title ? <span style={s.bulletStrong}>{title} </span> : null}
        {children}
      </span>
    </div>
  );
}

// A single movement pill (icon + label). Used on the Path A result
// screen to give each topic named in the body ("squeeze, relax, push")
// a visual anchor in its established brand colour.

function MovementPill({ Icon, label, color }) {
  return (
    <div style={s.movementPill}>
      <div style={s.movementIconTile}>
        <Icon color={color} />
      </div>
      <span style={{ ...s.movementLabel, color }}>{label}</span>
    </div>
  );
}

// ── Option card ─────────────────────────────────────────────────────────
// When an `Icon` component is supplied, the left accent bar is replaced
// by a 56px illustrated tile. Without an icon, the card falls back to
// the original left-bar variant so Q2 continues to look the way it did.

function OptionCard({ label, selected, onSelect, Icon }) {
  return (
    <button
      type="button"
      className="tap-card"
      style={{ ...s.option, ...(selected ? s.optionSelected : null) }}
      onClick={onSelect}
      aria-pressed={selected}
    >
      {Icon ? (
        <span style={{ ...s.iconTile, ...(selected ? s.iconTileSelected : null) }} aria-hidden="true">
          <Icon selected={selected} />
        </span>
      ) : (
        <span style={{ ...s.optionBar, ...(selected ? s.optionBarSelected : null) }} aria-hidden="true" />
      )}
      <span style={s.optionText}>{label}</span>
      {selected && (
        <span
          className="motion-pop"
          aria-hidden="true"
          style={{
            alignSelf: 'center',
            marginRight: 14,
            width: 22,
            height: 22,
            borderRadius: 11,
            background: 'var(--gold, #af004f)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          {'\u2713'}
        </span>
      )}
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export default function PathAssessment({ onComplete, onClose, onExit }) {
  const { user, userProgress } = useApp();
  const t = useStrings();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [q1Selection, setQ1Selection] = useState(null); // 'A' | 'B' | 'C' | 'D'
  const [q2Selection, setQ2Selection] = useState(null); // 'regular' | 'some' | 'none'
  const [saving, setSaving] = useState(false);
  // "Why this path?" disclosure on the result screen — collapsed by
  // default so curious users can dig in without bloating the screen
  // for the rest.
  const [whyOpen, setWhyOpen] = useState(false);
  useScreenTransition(currentScreen);

  const derivedPath = q1Selection === 'C' || q1Selection === 'D' ? 'B' : 'A';

  // First-name greeting on the result screen. firstNameFromUser
  // returns null for generic / non-name email handles (admin, test,
  // pw-test) so the title falls back to the unpersonalized line.
  const firstName = firstNameFromUser(user);

  // Pull the user's stated intent off userProgress (set by IntentPicker
  // earlier in onboarding). 'unspecified' / null both mean "no intent
  // chosen, no echo line."
  const userIntent = userProgress?.user_intent && userProgress.user_intent !== 'unspecified'
    ? userProgress.user_intent
    : null;

  // Celebration on the result screen: a one-shot warm bloom behind the
  // title plus a milestone haptic + chime so the path reveal lands as
  // a moment, not just another screen change. The chime is gated by
  // user_progress.sound_enabled (no-op when off); the haptic respects
  // prefers-reduced-motion. Fire once per visit to screen 3.
  useEffect(() => {
    if (currentScreen !== 3) return;
    hapticMilestone();
    playSessionCompleteChime();
  }, [currentScreen]);

  function goTo(screen) {
    hapticTap();
    setCurrentScreen(screen);
  }

  // OnboardingHeader handlers. Back on sub-screen 0 dismisses the
  // whole overlay (back to /home via the onClose prop threaded from
  // OnboardingFlow ← WeekHome); otherwise it decrements the sub-step.
  // Skip writes a neutral Path A assignment so the user lands on a
  // valid programme instead of a blank slate.
  function handleBack() {
    if (saving) return;
    if (currentScreen === 0) {
      if (onClose) onClose();
      return;
    }
    setCurrentScreen((n) => n - 1);
  }

  async function handleSkip() {
    if (saving) return;
    setSaving(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      // Defaulting to Path A keeps new users on a valid programme
      // when they skip the assessment, rather than leaving them
      // with no user_path and a broken day-1 resolver.
      data.user_path = 'A';
      data.path_assessment_skipped = true;
      data.path_assigned_at = new Date().toISOString();
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('PathAssessment skip save failed:', err);
      setSaving(false);
    }
  }

  async function finishAndContinue() {
    if (saving) return;
    hapticTap();
    setSaving(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.user_path = derivedPath;
      data.pelvic_history = q2Selection;
      data.path_assigned_at = new Date().toISOString();
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('PathAssessment save failed:', err);
      setSaving(false);
    }
  }

  const progressPct = ((currentScreen + 1) / 4) * 100;

  return (
    <div style={s.screen}>
      <OnboardingHeader onBack={handleBack} onSkip={handleSkip} onExit={onExit} />

      {/* Progress bar */}
      <div style={s.progressTrack} role="progressbar" aria-valuemin={0} aria-valuemax={4} aria-valuenow={currentScreen + 1}>
        <div style={{ ...s.progressFill, width: `${progressPct}%` }} />
      </div>

      {/* Screen content */}
      <div style={s.scroll}>
        <div style={s.container}>
          {currentScreen === 0 && (
            <div key={currentScreen} className="screen-enter">
              <div className="screen-enter-child"><HeroIllustration /></div>
              <h1 style={s.title} className="screen-enter-child">{t('path_assess_welcome_title')}</h1>
              <div style={s.bulletStack} role="list" className="screen-enter-child">
                <BulletCard
                  Icon={IconCompass}
                  className="stagger-item"
                  style={{ '--stagger-i': 0 }}
                >
                  {t('path_assess_intro_bullet_1')}
                </BulletCard>
                <BulletCard
                  Icon={IconSprout}
                  className="stagger-item"
                  style={{ '--stagger-i': 1 }}
                >
                  {t('path_assess_intro_bullet_2')}
                </BulletCard>
                <BulletCard
                  Icon={IconClock}
                  className="stagger-item"
                  style={{ '--stagger-i': 2 }}
                >
                  {t('path_assess_intro_bullet_3')}
                </BulletCard>
              </div>
            </div>
          )}

          {currentScreen === 1 && (
            <div key={currentScreen} className="screen-enter">
              <h1 style={s.title} className="screen-enter-child">{t('path_assess_q1_title')}</h1>
              <p style={s.subtitle} className="screen-enter-child">{t('path_assess_q1_sub')}</p>
              <div style={s.options} className="screen-enter-child">
                <OptionCard label={t('path_assess_q1_a')} selected={q1Selection === 'A'} onSelect={() => { hapticTap(); setQ1Selection('A'); }} Icon={IconReady} />
                <OptionCard label={t('path_assess_q1_b')} selected={q1Selection === 'B'} onSelect={() => { hapticTap(); setQ1Selection('B'); }} Icon={IconTension} />
                <OptionCard label={t('path_assess_q1_c')} selected={q1Selection === 'C'} onSelect={() => { hapticTap(); setQ1Selection('C'); }} Icon={IconCare} />
                <OptionCard label={t('path_assess_q1_d')} selected={q1Selection === 'D'} onSelect={() => { hapticTap(); setQ1Selection('D'); }} Icon={IconRecovery} />
              </div>
            </div>
          )}

          {currentScreen === 2 && (
            <div key={currentScreen} className="screen-enter">
              <h1 style={s.title} className="screen-enter-child">{t('path_assess_q2_title')}</h1>
              <div style={s.options} className="screen-enter-child">
                <OptionCard label={t('path_assess_q2_a')} selected={q2Selection === 'regular'} onSelect={() => { hapticTap(); setQ2Selection('regular'); }} Icon={IconCalendarCheck} />
                <OptionCard label={t('path_assess_q2_b')} selected={q2Selection === 'some'} onSelect={() => { hapticTap(); setQ2Selection('some'); }} Icon={IconSpark} />
                <OptionCard label={t('path_assess_q2_c')} selected={q2Selection === 'none'} onSelect={() => { hapticTap(); setQ2Selection('none'); }} Icon={IconSeed} />
              </div>
            </div>
          )}

          {currentScreen === 3 && (
            <div key={currentScreen} className="screen-enter">
              <div style={{ position: 'relative' }}>
                {/* One-shot rose bloom behind the path-reveal heading. Mounts
                    with the screen, runs a 1.1s warm halo, fades to nothing.
                    aria-hidden so AT focus stays on the eyebrow + title. */}
                <span className="onboard-bloom" aria-hidden="true" />
                <p style={s.eyebrow} className="screen-enter-child">
                  {firstName ? t('path_assess_result_eyebrow_named').replace('{name}', firstName) : t('path_assess_result_eyebrow')}
                </p>
                <h1 style={s.title} className="screen-enter-child">
                  {derivedPath === 'A' ? t('path_assess_result_a_title') : t('path_assess_result_b_title')}
                </h1>
                {userIntent && (
                  <p style={s.intentEcho} className="screen-enter-child">
                    {t(`path_assess_intent_echo_${derivedPath.toLowerCase()}`).replace(
                      '{intent}',
                      t(`intent_${userIntent}_short`)
                    )}
                  </p>
                )}
              </div>
              {derivedPath === 'A' && (
                <div style={s.movementRow} aria-hidden="true" className="screen-enter-child">
                  <MovementPill Icon={IconSqueeze} label={t('movement_squeeze')} color="#8B4A5A" />
                  <MovementPill Icon={IconRelax} label={t('movement_relax')} color="#7A5A6A" />
                  <MovementPill Icon={IconPush} label={t('movement_push')} color="#4a6a80" />
                </div>
              )}
              {derivedPath === 'B' && (
                <div style={s.movementRowDuo} aria-hidden="true" className="screen-enter-child">
                  <MovementPill Icon={IconBreath} label={t('pathb_breath_label')} color="#7A5A6A" />
                  <MovementPill Icon={IconRelease} label={t('pathb_release_label')} color="#6A7A8A" />
                </div>
              )}
              <div style={s.bulletStack} role="list" className="screen-enter-child">
                <BulletCard
                  Icon={IconTarget}
                  className="stagger-item"
                  style={{ '--stagger-i': 0 }}
                >
                  {derivedPath === 'A'
                    ? t('path_assess_result_a_bullet_1')
                    : t('path_assess_result_b_bullet_1')}
                </BulletCard>
                <BulletCard
                  Icon={IconPath}
                  className="stagger-item"
                  style={{ '--stagger-i': 1 }}
                >
                  {derivedPath === 'A'
                    ? t('path_assess_result_a_bullet_2')
                    : t('path_assess_result_b_bullet_2')}
                </BulletCard>
                <BulletCard
                  Icon={IconCalendar}
                  className="stagger-item"
                  style={{ '--stagger-i': 2 }}
                >
                  {derivedPath === 'A'
                    ? t('path_assess_result_a_bullet_3')
                    : t('path_assess_result_b_bullet_3')}
                </BulletCard>
              </div>
              {/* "Why this path?" disclosure. Default-collapsed so the
                  result screen stays tight; expanded on tap with a
                  short clinical reasoning line. Native <details> gives
                  us free aria-expanded + keyboard support; the chevron
                  rotates via the [open] attribute. Scroll the expanded
                  body into view when opening on mobile — without this
                  the disclosure sits at the bottom of the viewport,
                  expands below the fold, and the user sees nothing
                  change. */}
              <details
                className="screen-enter-child"
                style={s.whyDetails}
                onToggle={(e) => {
                  const el = e.currentTarget;
                  const isOpen = el.open;
                  setWhyOpen(isOpen);
                  if (isOpen) {
                    // requestAnimationFrame runs after React clears
                    // currentTarget, so capture the element first.
                    requestAnimationFrame(() => {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    });
                  }
                }}
              >
                <summary style={s.whySummary}>
                  <span>{t('path_assess_why_this_path')}</span>
                  <span aria-hidden="true" style={{ ...s.whyChevron, transform: whyOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    {'˅'}
                  </span>
                </summary>
                <p style={s.whyBody}>
                  {derivedPath === 'A' ? t('path_assess_why_a_body') : t('path_assess_why_b_body')}
                </p>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* Footer CTA */}
      <div style={s.footer}>
        {currentScreen === 0 && (
          <button type="button" style={{ ...s.cta, ...s.ctaEnabled }} onClick={() => goTo(1)}>
            {t('path_assess_welcome_cta')}
          </button>
        )}

        {currentScreen === 1 && (
          <button
            type="button" style={{ ...s.cta, ...(q1Selection ? s.ctaEnabled : s.ctaDisabled) }}
            onClick={() => q1Selection && goTo(2)}
            disabled={!q1Selection}
          >
            {t('path_assess_next')}
          </button>
        )}

        {currentScreen === 2 && (
          <button
            type="button" style={{ ...s.cta, ...(q2Selection ? s.ctaEnabled : s.ctaDisabled) }}
            onClick={() => q2Selection && goTo(3)}
            disabled={!q2Selection}
          >
            {t('path_assess_see_path')}
          </button>
        )}

        {currentScreen === 3 && (
          <button
            type="button" style={{ ...s.cta, ...(saving ? s.ctaDisabled : s.ctaEnabled) }}
            onClick={finishAndContinue}
            disabled={saving}
          >
            {t('path_assess_begin')}
          </button>
        )}
      </div>
    </div>
  );
}
